import { Component, Input, OnInit } from "@angular/core";
import * as moment from "moment";
import { AppService } from "src/app/core/services/app.service";
import { SalesSummary } from "src/app/shared/models/focusDataModels/SalesSummary";

@Component({
	selector: "app-sales-summary-line-chart",
	templateUrl: "./sales-summary-line-chart.component.html",
	styleUrls: ["./sales-summary-line-chart.component.scss"],
})
export class SalesSummaryLineChartComponent implements OnInit {
	moment: any = moment;
	orderChartData;
	orderChartOptions;
	@Input() salesSummaries: SalesSummary[];

	constructor(public app: AppService) {}

	ngOnInit(): void {
		this.orderChartBuild();
	}

	async changeDataset(event) {
		switch (event) {
			case "orderType":
				this.xBuildChartOrderType();
				break;
			case "reportGroup":
				this.xBuildChartReportGroup();
				break;
			case "salesVLabor":
				this.xBuildChartSalesVLabor();
				break;
			case "ppcPpa":
				this.xBuildChartPpcPpa();
				break;
			default:
				this.xBuildChartOrderType();
				break;
		}
	}

	async orderChartBuild() {
		this.orderChartOptions = {
			legend: {
				display: true,
			},
			responsive: true,
			hover: {
				mode: "index",
			},
		};

		await this.xBuildChartOrderType();
	}

	async xBuildChartOrderType() {
		const otRecord: any[] = [];
		const otId: number[] = [];
		const otDays: string[] = [];
		for (let businessDate of this.salesSummaries) {
			for (let ot of businessDate.OrderTypeSummaries) {
				const otIdIdx = otId.findIndex((i) => i === ot.ID);
				if (otIdIdx === -1) {
					otId.push(ot.ID);
				}
				const otDayIdx = otDays.findIndex((i) => i === ot.CheckDate);
				if (otDayIdx === -1) {
					otDays.push(ot.CheckDate);
				}
				otRecord.push({
					name: ot.OrderType.Name,
					id: ot.ID,
					count: ot.OTCount,
					revenue: ot.OTSales,
					checkDate: ot.CheckDate,
				});
			}
		}

		this.orderChartData = {
			labels: otDays
				.sort((a, b) => (moment(a, "MMDDYYYY").unix() > moment(b, "MMDDYYYY").unix() ? 1 : -1))
				.map((i) => moment(i, "MMDDYYYY").format("dddd")),
			datasets: [],
		};

		for (let ot of otId) {
			const colors = this.app.references.pastelColorGenerator(1);
			const otFilter = otRecord.filter((i) => i.id === ot);
			this.orderChartData.datasets.push({
				label: otFilter[0].name,
				data: otFilter.map((i) => i.revenue),
				borderColor: colors[0],
				backgroundColor: this.app.references.transparentize(colors[0], 0.5),
				borderWidth: 3,
				fill: true,
			});
		}
	}

	async xBuildChartReportGroup() {
		const rgRecord: any[] = [];
		const rgId: number[] = [];
		const rgDays: string[] = [];
		for (let businessDate of this.salesSummaries) {
			for (let rg of businessDate.ReportGroupSummaries) {
				const rdIdIdx = rgId.findIndex((i) => i === rg.ID);
				if (rdIdIdx === -1) {
					rgId.push(rg.ID);
				}
				const rgDayIdx = rgDays.findIndex((i) => i === rg.CheckDate);
				if (rgDayIdx === -1) {
					rgDays.push(rg.CheckDate);
				}
				rgRecord.push({
					name: rg.ReportGroup.Name,
					id: rg.ID,
					count: rg.RGCount,
					revenue: rg.RGSales,
					checkDate: rg.CheckDate,
				});
			}
		}

		this.orderChartData = {
			labels: rgDays
				.sort((a, b) => (moment(a, "MMDDYYYY").unix() > moment(b, "MMDDYYYY").unix() ? 1 : -1))
				.map((i) => moment(i, "MMDDYYYY").format("dddd")),
			datasets: [],
		};

		for (let ot of rgId) {
			const otFilter = rgRecord.filter((i) => i.id === ot);
			const color = this.app.references.pastelColorGenerator(1);
			this.orderChartData.datasets.push({
				label: otFilter[0].name,
				data: otFilter.map((i) => i.revenue),
				borderColor: color[0],
				borderWidth: 3,
				backgroundColor: this.app.references.transparentize(color[0], 0.5),
				fill: true,
			});
		}
	}

	async xBuildChartSalesVLabor() {
		const color = this.app.references.pastelColorGenerator(2);
		this.orderChartData = {
			labels: this.salesSummaries
				.sort((a, b) => (moment(a.CheckDate, "MMDDYYYY").unix() > moment(b.CheckDate, "MMDDYYYY").unix() ? 1 : -1))
				.map((i) => moment(i.CheckDate, "MMDDYYYY").format("dddd")),
			datasets: [
				{
					label: "Net Sales",
					data: this.salesSummaries.map((i) => i.NetSales),
					borderColor: color[0],
					borderWidth: 3,
					backgroundColor: this.app.references.transparentize(color[0], 0.5),
					fill: true,
				},
				{
					label: "Labor Cost",
					data: this.salesSummaries.map((i) => i.LaborCost),
					borderColor: color[1],
					borderWidth: 3,
					backgroundColor: this.app.references.transparentize(color[1], 0.5),
					fill: true,
				},
			],
		};
	}

	async xBuildChartPpcPpa() {
		const color = this.app.references.pastelColorGenerator(2);
		this.orderChartData = {
			labels: this.salesSummaries
				.sort((a, b) => (moment(a.CheckDate, "MMDDYYYY").unix() > moment(b.CheckDate, "MMDDYYYY").unix() ? 1 : -1))
				.map((i) => moment(i.CheckDate, "MMDDYYYY").format("dddd")),
			datasets: [
				{
					label: `Per Check Average`,
					data: this.salesSummaries.map((i) => i.AverageCheck),
					borderColor: color[0],
					borderWidth: 3,
					backgroundColor: this.app.references.transparentize(color[0], 0.5),
					fill: true,
				},
				{
					label: "Per Person Average",
					data: this.salesSummaries.map((i) => i.AverageGuest),
					borderColor: color[1],
					borderWidth: 3,
					backgroundColor: this.app.references.transparentize(color[1], 0.5),
					fill: true,
				},
			],
		};
	}
}
